import React, { useState, useEffect } from 'react';
import { 
  Search, 
  UserPlus, 
  Phone,
  FileText,
  Home,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Edit3,
  ChevronRight,
  Globe,
  CreditCard,
  QrCode
} from 'lucide-react';
import { Worker, Team, RoleHierarchy, COUNTRIES } from '../../types';
import { QRBadgeModal } from './QRBadgeModal';
import { WorkerModal } from './WorkerModal';

interface WorkerListProps {
  onWorkersUpdated?: () => void;
}

export const WorkerList: React.FC<WorkerListProps> = ({ onWorkersUpdated }) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [teamFilter, setTeamFilter] = useState('');
  const [hierarchyFilter, setHierarchyFilter] = useState<string>('');
  const [citizenshipFilter, setCitizenshipFilter] = useState<string>('');
  const [docStatusFilter, setDocStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  // Modals & Selected items
  const [selectedWorkerForQr, setSelectedWorkerForQr] = useState<Worker | null>(null);
  const [selectedWorkerForEdit, setSelectedWorkerForEdit] = useState<Worker | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const fetchWorkers = () => {
    setLoading(true);
    let url = '/api/v1/hr/workers?';
    if (teamFilter) url += `teamId=${teamFilter}&`;
    if (search) url += `search=${encodeURIComponent(search)}&`;

    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.success) setWorkers(d.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetch('/api/v1/hr/teams')
      .then(r => r.json())
      .then(d => {
        if (d.success) setTeams(d.data);
      });
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [teamFilter, search]);

  // Helper to determine hierarchy role from position if not explicitly set
  const getWorkerHierarchy = (w: Worker): RoleHierarchy => {
    if (w.roleHierarchy) return w.roleHierarchy;
    const pos = (w.position || '').toLowerCase();
    if (pos.includes('прораб') || pos.includes('форемен') || pos.includes('начальник')) return 'foreman';
    if (pos.includes('бригадир') || pos.includes('старший')) return 'brigadier';
    if (pos.includes('помощник') || pos.includes('разнорабочий') || pos.includes('подсоб')) return 'helper';
    return 'master';
  };

  // Helper for rendering Hierarchy badge
  const renderHierarchyBadge = (role: RoleHierarchy) => {
    switch (role) {
      case 'foreman':
        return (
          <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-xl text-xs font-bold shadow-sm">
            👑 Форемен (Прораб)
          </span>
        );
      case 'brigadier':
        return (
          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-xl text-xs font-bold shadow-sm">
            👨‍✈️ Бригадир
          </span>
        );
      case 'master':
        return (
          <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-xl text-xs font-bold shadow-sm">
            🛠️ Мастер
          </span>
        );
      case 'helper':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 rounded-xl text-xs font-bold shadow-sm">
            🦺 Помощник
          </span>
        );
    }
  };

  // Patent / Registration Expiry status calculator
  const getPatentStatus = (expiryDate?: string) => {
    if (!expiryDate) return { label: 'Нет данных', color: 'bg-slate-100 text-slate-500 border-slate-200', code: 'none' };
    const expiry = new Date(expiryDate).getTime();
    const now = Date.now();
    const days = Math.ceil((expiry - now) / (1000 * 3600 * 24));

    if (days < 0) {
      return { label: `Просрочен (${Math.abs(days)} дн. назад)`, color: 'bg-rose-50 text-rose-700 border-rose-200 font-bold', code: 'expired', days };
    }
    if (days <= 30) {
      return { label: `Истекает через ${days} дн.`, color: 'bg-amber-50 text-amber-800 border-amber-200 font-bold', code: 'expiring', days };
    }
    return { label: `Действует до ${expiryDate}`, color: 'bg-emerald-50 text-emerald-700 border-emerald-200 font-medium', code: 'active', days };
  };

  // Helper for country metadata
  const getCountry = (w: Worker) => {
    const key = (w.citizenship || 'uzbekistan').toLowerCase();
    return COUNTRIES[key] || COUNTRIES.uzbekistan;
  };

  // Filtered workers list
  const filteredWorkers = workers.filter(w => {
    const role = getWorkerHierarchy(w);
    if (hierarchyFilter && role !== hierarchyFilter) return false;

    const country = getCountry(w);
    if (citizenshipFilter && country.code !== citizenshipFilter) return false;

    if (docStatusFilter) {
      const patentStat = getPatentStatus(w.patentExpiryDate || w.patentExpiry);
      if (docStatusFilter === 'expired' && patentStat.code !== 'expired') return false;
      if (docStatusFilter === 'expiring' && patentStat.code !== 'expiring') return false;
    }
    return true;
  });

  return (
    <div className="space-y-4 w-full">
      {/* Controls Bar - Light Fintech styling */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/90 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] w-full">
        <div className="flex-1 flex flex-wrap items-center gap-2.5">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Поиск по ФИО, паспорту, телефону, прописке..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-amber-500 focus:bg-white transition"
            />
          </div>

          {/* Citizenship Filter */}
          <select
            value={citizenshipFilter}
            onChange={(e) => setCitizenshipFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:outline-none focus:border-sky-500"
          >
            <option value="">Все страны (Гражданство)</option>
            {Object.values(COUNTRIES).map(c => (
              <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
            ))}
          </select>

          {/* Hierarchy Filter */}
          <select
            value={hierarchyFilter}
            onChange={(e) => setHierarchyFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:outline-none focus:border-amber-500"
          >
            <option value="">Все роли (Иерархия)</option>
            <option value="foreman">👑 Форемен (Прораб)</option>
            <option value="brigadier">👨‍✈️ Бригадир</option>
            <option value="master">🛠️ Мастер</option>
            <option value="helper">🦺 Помощник</option>
          </select>

          {/* Team/Brigade Filter */}
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-amber-500"
          >
            <option value="">Все бригады</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name.replace('Звено', 'Бригада')}</option>)}
          </select>

          {/* Document Status Filter */}
          <select
            value={docStatusFilter}
            onChange={(e) => setDocStatusFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-amber-500"
          >
            <option value="">Статус документов</option>
            <option value="expiring">⚠️ Истекают патенты (&lt;30 дней)</option>
            <option value="expired">🚨 Просроченные патенты</option>
          </select>
        </div>

        {/* Add Worker Button */}
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 px-4 py-2.5 rounded-xl text-xs font-bold shadow-md shadow-amber-500/20 transition active:scale-95 shrink-0"
        >
          <UserPlus className="w-4 h-4" />
          <span>Добавить сотрудника</span>
        </button>
      </div>

      {/* Spacious Full-Width Table */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm bg-white rounded-2xl border border-slate-200">
          Загрузка картотеки кадров...
        </div>
      ) : filteredWorkers.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 text-slate-500 text-sm">
          Сотрудников по заданным критериям не найдено.
        </div>
      ) : (
        <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-sm w-full">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-xs border-collapse min-w-[1100px]">
              <thead>
                <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold uppercase tracking-wider text-[11px]">
                  <th className="p-3.5 pl-4">Сотрудник & Флаг Страны</th>
                  <th className="p-3.5">Гражданство & Паспорт</th>
                  <th className="p-3.5">Иерархическая роль</th>
                  <th className="p-3.5">Прикрепленная бригада</th>
                  <th className="p-3.5">Патент (Срок действия)</th>
                  <th className="p-3.5">Регистрация / Прописка</th>
                  <th className="p-3.5">Телефон / Связь</th>
                  <th className="p-3.5 text-center pr-4">Действия / Бейдж</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800">
                {filteredWorkers.map((w) => {
                  const role = getWorkerHierarchy(w);
                  const patentStat = getPatentStatus(w.patentExpiryDate || w.patentExpiry);
                  const country = getCountry(w);
                  const passportNum = w.passportNumber || 'FA 9841029';
                  const regAddress = w.registrationAddress || 'г. Москва, ул. Полярная, д. 42 (Временная)';
                  const regExpiry = w.registrationExpiryDate || '2027-01-01';
                  const teamDisplayName = (w.team_name || 'Бригада №1').replace('Звено', 'Бригада');

                  return (
                    <tr
                      key={w.id}
                      onClick={() => setSelectedWorkerForEdit(w)}
                      className="hover:bg-slate-50/80 transition cursor-pointer group"
                    >
                      {/* Full Name, Avatar & Flag Emoji */}
                      <td className="p-3.5 pl-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden group-hover:border-amber-400 transition">
                              {w.photoUrl ? (
                                <img src={w.photoUrl} alt={w.fullName} className="w-full h-full object-cover" />
                              ) : (
                                <span>{w.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}</span>
                              )}
                            </div>
                            {/* Country Flag Badge over Avatar */}
                            <span className="absolute -bottom-1 -right-1 text-base leading-none bg-white rounded-full p-0.5 shadow-sm border border-slate-200" title={`Гражданство: ${country.name}`}>
                              {country.flag}
                            </span>
                          </div>

                          <div>
                            <div className="font-bold text-slate-900 text-sm group-hover:text-amber-600 transition flex items-center gap-1.5">
                              <span>{w.fullName}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-amber-500 transition" />
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 font-medium flex items-center gap-1">
                              <span className="text-sm">{country.flag}</span>
                              <span className="font-semibold text-slate-700">{country.name}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Citizenship & Passport Number */}
                      <td className="p-3.5">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-mono font-bold text-slate-800">
                              {passportNum}
                            </span>
                          </div>
                          <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 rounded-lg text-[11px] font-bold">
                            {country.flag} {country.name}
                          </span>
                        </div>
                      </td>

                      {/* Hierarchy Badge */}
                      <td className="p-3.5">
                        {renderHierarchyBadge(role)}
                      </td>

                      {/* Brigade Assignment */}
                      <td className="p-3.5">
                        <span className="font-bold text-slate-800 bg-slate-100 px-2.5 py-1 rounded-xl border border-slate-200">
                          {teamDisplayName}
                        </span>
                      </td>

                      {/* Patent Expiry Status */}
                      <td className="p-3.5">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-mono font-semibold text-slate-700">
                              {w.patentNumber || 'ПАТ-2026/8492'}
                            </span>
                          </div>
                          <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded-lg text-[11px] ${patentStat.color}`}>
                            {patentStat.code === 'expired' && <AlertTriangle className="w-3 h-3 text-rose-600 shrink-0" />}
                            {patentStat.code === 'expiring' && <Clock className="w-3 h-3 text-amber-600 shrink-0" />}
                            {patentStat.code === 'active' && <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />}
                            <span>{patentStat.label}</span>
                          </span>
                        </div>
                      </td>

                      {/* Registration Address */}
                      <td className="p-3.5">
                        <div className="space-y-1 max-w-[200px]">
                          <div className="text-slate-800 font-medium truncate flex items-center gap-1" title={regAddress}>
                            <Home className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{regAddress}</span>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            До: <span className="font-semibold text-slate-700">{regExpiry}</span>
                          </div>
                        </div>
                      </td>

                      {/* Phone Number */}
                      <td className="p-3.5">
                        <a
                          href={`tel:${w.phone || '+79165550100'}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 text-slate-700 hover:text-amber-600 font-bold bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl transition"
                        >
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          <span>{w.phone || '+7 (916) 555-01-00'}</span>
                        </a>
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 text-center pr-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedWorkerForQr(w)}
                            className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 px-2.5 py-1.5 rounded-xl font-bold transition active:scale-95"
                            title="Печать QR-бейджа"
                          >
                            <QrCode className="w-3.5 h-3.5 text-slate-600" />
                            <span>QR</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setSelectedWorkerForEdit(w)}
                            className="inline-flex items-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-1.5 rounded-xl font-bold transition active:scale-95"
                            title="Редактировать сотрудника"
                          >
                            <Edit3 className="w-3.5 h-3.5 text-amber-700" />
                            <span>Редактировать</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedWorkerForQr && (
        <QRBadgeModal
          worker={selectedWorkerForQr}
          onClose={() => setSelectedWorkerForQr(null)}
        />
      )}

      {(isAddModalOpen || selectedWorkerForEdit) && (
        <WorkerModal
          teams={teams}
          worker={selectedWorkerForEdit || undefined}
          onClose={() => {
            setIsAddModalOpen(false);
            setSelectedWorkerForEdit(null);
          }}
          onSuccess={() => {
            fetchWorkers();
            setIsAddModalOpen(false);
            setSelectedWorkerForEdit(null);
            if (onWorkersUpdated) onWorkersUpdated();
          }}
        />
      )}
    </div>
  );
};
